import { PRODUCT_NAME } from './brand.ts';
import type { AnyLocale } from './locales.ts';

/**
 * Küçük çeviri çalışma zamanı (bütün yüzeyler: widget, panel, site, mobil).
 * - Yer tutucu: `"Merhaba {name}"` → `t('greeting', { name: 'Ada' })`; sayılar dile göre biçimlenir.
 * - Çoğul: `messages_one` / `messages_other` (+ `_zero/_two/_few/_many`) anahtarları;
 *   `t('messages', { count: 3 })` → `Intl.PluralRules` dile göre doğru biçimi seçer (Arapça, Rusça…).
 * - Verilmeyen yer tutucu olduğu gibi kalır; `splitSlots` bunu biçimli öğe yuvası olarak kullanır.
 */
export type PluralCategory = Intl.LDMLPluralRule;
export type MessageMap = Readonly<Record<string, string>>;

type PluralBase<K extends string> = K extends `${infer Base}_${PluralCategory}` ? Base : never;

/** Kaynak (İngilizce) kataloğun anahtarları; çoğul biçimler tek anahtar olarak görünür. */
export type MessageKey<Source extends MessageMap> =
  Exclude<keyof Source & string, `${string}_${PluralCategory}`> | PluralBase<keyof Source & string>;

export type Params = Readonly<Record<string, string | number>>;

export type Translate<Source extends MessageMap> = {
  (key: MessageKey<Source>, params?: Params): string;
  /**
   * Dışarıdan gelen bir anahtar (ör. API'nin hata kodu) katalogda var mı? Varsa tipi daralır ve
   * doğrudan çevrilebilir; yoksa çağıran genel bir metne düşer.
   */
  readonly has: (key: string) => key is MessageKey<Source>;
};

/** `{name}` yer tutucusu; yuvalar (slots.ts) da aynı sözdizimini kullanır. */
export const PLACEHOLDER = /\{(\w+)\}/g;

/** Her mesajda kullanılabilen ortak yer tutucular. */
const GLOBAL_PARAMS: Params = { product: PRODUCT_NAME };

export function createTranslator<Source extends MessageMap>(
  locale: AnyLocale,
  messages: MessageMap,
): Translate<Source> {
  const plural = new Intl.PluralRules(locale);
  const number = new Intl.NumberFormat(locale);

  const format = (value: string | number | undefined, name: string) =>
    value === undefined ? `{${name}}` : typeof value === 'number' ? number.format(value) : value;

  const translate = (key: MessageKey<Source>, params?: Params): string => {
    const count = params?.['count'];
    const template =
      messages[key] ??
      (typeof count === 'number'
        ? (messages[`${key}_${plural.select(count)}`] ?? messages[`${key}_other`])
        : undefined);
    // Eksik anahtar katalog testinde yakalanır; çalışma zamanında anahtarın kendisi görünür.
    if (template === undefined) return key;
    return template.replace(PLACEHOLDER, (_match, name: string) =>
      format(params?.[name] ?? GLOBAL_PARAMS[name], name),
    );
  };

  const has = (key: string): key is MessageKey<Source> =>
    key in messages || `${key}_other` in messages;

  return Object.assign(translate, { has });
}
